import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BdcNavGauge — Sep 2026
 *
 * Replaces the "BDC — DDM Bypassed / NAV gauge coming soon" placeholder
 * text inside the Valuation Matrix. Two horizontal strips:
 *
 *   1. Price / NAV     — where the stock trades relative to its book /
 *                        NAV per share, in 5 zones (deep-discount →
 *                        deep-premium). BDCs are book-value businesses,
 *                        so this is the accepted anchor.
 *
 *   2. NII coverage    — trailing quarterly Net Investment Income vs
 *                        dividends paid. Anything ≥ 1.00 means the
 *                        dividend is being earned. This is what the
 *                        "payout 143%" yellow banner should really
 *                        show for a BDC (GAAP EPS payout is meaningless
 *                        because a BDC's investment income arrives as
 *                        NII, not as GAAP earnings).
 *
 * Defensive contract — the gauge NEVER breaks the page:
 *   • Missing / zero / NaN NAV → Price/NAV strip is hidden.
 *   • Missing / zero / NaN price → same.
 *   • Missing / non-ok sec_coverage → NII strip is hidden.
 *   • Individual quarter with a null ratio → skipped in the sparkline.
 *   • Both sections unavailable → the component renders NOTHING and
 *     the caller falls back to the plain text reason line, so we
 *     never regress vs the pre-gauge behaviour.
 */

const PNAV_ZONES = [
  { max: 0.85, label: 'Deep Disc', tone: 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/15 ring-emerald-500/40' },
  { max: 0.95, label: 'Discount',  tone: 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 ring-emerald-400/30' },
  { max: 1.05, label: 'Fair',      tone: 'text-foreground bg-muted/50 ring-border' },
  { max: 1.20, label: 'Premium',   tone: 'text-amber-700 dark:text-amber-300 bg-amber-500/10 ring-amber-400/40' },
  { max: Infinity, label: 'Deep Premium', tone: 'text-rose-700 dark:text-rose-300 bg-rose-500/15 ring-rose-500/40' },
];

function classifyPNav(r) {
  for (const z of PNAV_ZONES) if (r <= z.max) return z;
  return PNAV_ZONES[PNAV_ZONES.length - 1];
}

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// Position a value on a 0.6× — 1.4× horizontal scale (covers deep disc
// through deep premium comfortably). Clamped to 0–100%.
function pnavPct(r) {
  const min = 0.6;
  const max = 1.4;
  const clamped = Math.max(min, Math.min(max, r));
  return ((clamped - min) / (max - min)) * 100;
}

export default function BdcNavGauge({ stock, livePrice }) {
  // --- Inputs, all null-safe ------------------------------------------
  const nav = safeNum(stock?.book_value);
  const priceFromStock = safeNum(stock?.price) ?? safeNum(stock?.regular_market_price) ?? safeNum(stock?.current_price);
  const priceLive = safeNum(livePrice);
  const price = (priceLive && priceLive > 0) ? priceLive : priceFromStock;

  const cov = stock?.sec_coverage;
  const covOk = cov && typeof cov === 'object' && cov.status === 'ok' && (cov.asset_class === 'BDC' || !cov.asset_class);
  const covRatio = covOk ? safeNum(cov.ratio_current) : null;
  const covTrend = covOk ? safeNum(cov.trend_pct) : null;
  const covQuarters = (covOk && Array.isArray(cov.quarters)) ? cov.quarters : [];
  const covLatestEnd = covOk ? (cov.latest_quarter_end || null) : null;

  // Sep 2026 — NAV history micro-chart. Fetches quarterly NAV/share
  // from a dedicated endpoint (only called when this component mounts
  // for a BDC, so non-BDCs never pay this cost). Any fetch failure
  // silently returns an empty array so the trend chart auto-hides.
  const [navPoints, setNavPoints] = useState([]);
  const [navGrowthPct, setNavGrowthPct] = useState(null);
  useEffect(() => {
    if (!stock?.ticker) return undefined;
    let cancelled = false;
    setNavPoints([]);
    setNavGrowthPct(null);
    axios.get(`${API}/stocks/${stock.ticker}/nav-history?quarters=5`)
      .then((r) => {
        if (cancelled) return;
        const pts = (r?.data?.points || [])
          .map((p) => ({ end: p?.end, nav: safeNum(p?.nav_per_share) }))
          .filter((p) => p.end && p.nav != null && p.nav > 0);
        setNavPoints(pts);
        setNavGrowthPct(safeNum(r?.data?.growth_pct));
      })
      .catch(() => {
        if (!cancelled) {
          setNavPoints([]);
          setNavGrowthPct(null);
        }
      });
    return () => { cancelled = true; };
  }, [stock?.ticker]);

  // --- Derived --------------------------------------------------------
  const pNavRatio = (nav && nav > 0 && price && price > 0) ? price / nav : null;
  const zone = pNavRatio != null ? classifyPNav(pNavRatio) : null;
  const discountPct = pNavRatio != null ? (1 - pNavRatio) * 100 : null; // positive = discount

  // Sparkline points — only keep finite positive-numerator entries.
  const sparkPoints = useMemo(() => {
    return covQuarters
      .map((q) => ({ end: q?.end, ratio: safeNum(q?.ratio) }))
      .filter((p) => p.ratio != null && p.ratio >= 0);
  }, [covQuarters]);

  const hasPNav = pNavRatio != null;
  const hasCov = covRatio != null && covRatio >= 0;

  // If NEITHER strip is available, render nothing — the caller shows
  // the plain-text reason as a fallback.
  if (!hasPNav && !hasCov) return null;

  return (
    <div className="mt-2 pt-2 border-t border-amber-300/40 dark:border-amber-700/30 space-y-2.5" data-testid="bdc-nav-gauge">

      {/* ---------------- Price / NAV strip ---------------- */}
      {hasPNav && (
        <div data-testid="bdc-pnav-strip">
          <div className="flex items-center justify-between flex-wrap gap-1.5 mb-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wide text-amber-800 dark:text-amber-300">
                Price / NAV
              </span>
              <span
                className="text-xs font-mono font-bold text-foreground tabular-nums"
                data-testid="bdc-pnav-value"
                title={`Price $${price.toFixed(2)} ÷ NAV $${nav.toFixed(2)} = ${pNavRatio.toFixed(3)}×`}
              >
                {pNavRatio.toFixed(2)}×
              </span>
              {/* Sep 2026 — NAV Growth health chip. Positive 5Q trend
                  = expanding loan book (healthy for a BDC). Negative
                  = shrinking book (needs the amber warning below). */}
              {navGrowthPct != null && navPoints.length >= 2 && (
                <span
                  className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ring-1 cursor-help ${
                    navGrowthPct >= 3
                      ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/15 ring-emerald-400/40'
                      : navGrowthPct >= 0
                        ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/5 ring-emerald-400/30'
                        : navGrowthPct >= -5
                          ? 'text-amber-700 dark:text-amber-300 bg-amber-500/10 ring-amber-400/40'
                          : 'text-rose-700 dark:text-rose-300 bg-rose-500/15 ring-rose-500/40'
                  }`}
                  data-testid="bdc-nav-growth-chip"
                  title={
                    navGrowthPct >= 3
                      ? 'Book is EXPANDING — loan portfolio grew ≥3% over 5 quarters. Positive signal for a BDC.'
                      : navGrowthPct >= 0
                        ? 'Book is STABLE — loan portfolio essentially flat over 5 quarters. Neutral signal.'
                        : navGrowthPct >= -5
                          ? 'Book is SLIGHTLY SHRINKING — loan portfolio down slightly. Monitor NII coverage.'
                          : 'Book is SHRINKING FAST — a declining NAV combined with a flat/rising dividend is a classic yield-trap setup for a BDC.'
                  }
                >
                  Book {navGrowthPct >= 0 ? '▲' : '▼'} {navGrowthPct >= 0 ? '+' : ''}{navGrowthPct.toFixed(1)}%
                  <span aria-hidden className="opacity-70">ⓘ</span>
                </span>
              )}
            </div>
            <span
              className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ring-1 cursor-help ${zone.tone}`}
              data-testid="bdc-pnav-bucket"
              title={pNavRatio >= 1.05
                ? 'Trading above NAV — you are paying more than the balance-sheet value of the underlying loan book. Historically premium tends to mean-revert.'
                : pNavRatio <= 0.95
                  ? 'Trading below NAV — you are paying less than the balance-sheet value of the underlying loan book. Could be a mispricing OR a warning about credit quality.'
                  : 'Trading close to NAV — the market is valuing the loan book roughly at carrying value.'}
            >
              {zone.label}
              <span aria-hidden className="opacity-70">ⓘ</span>
            </span>
          </div>

          {/* Gauge bar */}
          <div className="relative h-6" data-testid="bdc-pnav-bar">
            <div
              className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-2 rounded-full border border-border/40"
              style={{
                background: 'linear-gradient(to right, rgba(16, 185, 129, 0.45) 0%, rgba(132, 204, 22, 0.35) 25%, rgba(148, 163, 184, 0.30) 50%, rgba(245, 158, 11, 0.35) 75%, rgba(244, 63, 94, 0.45) 100%)',
              }}
            />
            {/* Zone boundaries at 0.85, 0.95, 1.05, 1.20 */}
            {[0.85, 0.95, 1.00, 1.05, 1.20].map((tick) => {
              const tp = pnavPct(tick);
              const isPar = tick === 1.00;
              return (
                <div
                  key={tick}
                  className={`absolute top-1/2 -translate-y-1/2 ${isPar ? 'h-3 w-px bg-foreground/70' : 'h-2 w-px bg-border'}`}
                  style={{ left: `${tp}%` }}
                />
              );
            })}
            {/* Current marker */}
            <div
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-foreground border-2 border-card shadow"
              style={{ left: `${pnavPct(pNavRatio)}%` }}
              data-testid="bdc-pnav-dot"
              title={`${pNavRatio.toFixed(3)}× NAV`}
            />
          </div>

          {/* Scale labels */}
          <div className="flex items-center justify-between text-[9px] font-mono text-muted-foreground mt-0.5">
            <span>0.60×</span>
            <span>0.85×</span>
            <span>0.95×</span>
            <span className="font-bold text-foreground">1.00</span>
            <span>1.05×</span>
            <span>1.20×</span>
            <span>1.40×</span>
          </div>

          {/* Facts line */}
          <p className="text-[10px] text-muted-foreground italic leading-relaxed mt-1">
            NAV / share <span className="font-semibold text-foreground">${nav.toFixed(2)}</span>{' '}
            · Price <span className="font-semibold text-foreground">${price.toFixed(2)}</span>{' '}
            ·{' '}
            {discountPct >= 0
              ? <>discount <span className="font-semibold text-emerald-600 dark:text-emerald-400">{discountPct.toFixed(1)}%</span></>
              : <>premium <span className="font-semibold text-amber-600 dark:text-amber-400">{Math.abs(discountPct).toFixed(1)}%</span></>}
          </p>

          {/* NAV history micro-chart — bars scaled to the min/max of
              the returned points so the direction reads at a glance.
              Hidden if fewer than 2 quarters of data (nothing to
              compare). Each bar's tooltip surfaces the exact NAV
              and quarter-end date. */}
          {navPoints.length >= 2 && (() => {
            const navs = navPoints.map((p) => p.nav);
            const lo = Math.min(...navs);
            const hi = Math.max(...navs);
            const span = hi - lo || 1;
            const first = navs[0];
            const last = navs[navs.length - 1];
            const deltaPct = ((last - first) / first) * 100;
            const trendUp = last >= first;
            return (
              <div className="mt-2" data-testid="bdc-nav-trend">
                <div className="flex items-center justify-between text-[9px] font-mono uppercase tracking-wider text-muted-foreground mb-0.5">
                  <span>NAV / share · last {navPoints.length}Q</span>
                  <span
                    className={`font-bold ${trendUp ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}
                    title={`${first.toFixed(2)} → ${last.toFixed(2)}`}
                  >
                    {trendUp ? '▲' : '▼'} {deltaPct >= 0 ? '+' : ''}{deltaPct.toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-end gap-1 h-8">
                  {navPoints.map((p, i) => {
                    // Height maps [lo, hi] → [15%, 100%] so even a
                    // flat quarter shows a visible bar. `lo === hi`
                    // guarded by `span || 1`.
                    const h = 15 + ((p.nav - lo) / span) * 85;
                    return (
                      <div
                        key={p.end || i}
                        className={`flex-1 min-w-[8px] rounded-t ${trendUp ? 'bg-emerald-500/60' : 'bg-rose-500/60'}`}
                        style={{ height: `${h}%` }}
                        title={`${p.end}: $${p.nav.toFixed(2)}`}
                      />
                    );
                  })}
                </div>
                <div className="flex items-center justify-between text-[9px] font-mono text-muted-foreground mt-0.5">
                  <span>{navPoints[0].end}</span>
                  <span>{navPoints[navPoints.length - 1].end}</span>
                </div>
                {!trendUp && (
                  <p className="text-[10px] text-amber-700 dark:text-amber-300 leading-relaxed mt-1">
                    Loan book is shrinking. A declining NAV with a flat/rising dividend puts pressure on the payout — watch NII coverage closely.
                  </p>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* ---------------- NII coverage strip ---------------- */}
      {hasCov && (
        <div data-testid="bdc-nii-coverage-strip">
          <div className="flex items-center justify-between flex-wrap gap-1.5 mb-1">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wide text-amber-800 dark:text-amber-300">
                NII Coverage
              </span>
              <span
                className="text-xs font-mono font-bold text-foreground tabular-nums"
                data-testid="bdc-nii-coverage-value"
                title={covLatestEnd ? `Trailing quarter ended ${covLatestEnd}` : undefined}
              >
                {covRatio.toFixed(2)}×
              </span>
            </div>
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ring-1 cursor-help ${
                covRatio >= 1.05
                  ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 ring-emerald-400/40'
                  : covRatio >= 1.0
                    ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/5 ring-emerald-400/30'
                    : covRatio >= 0.90
                      ? 'text-amber-700 dark:text-amber-300 bg-amber-500/10 ring-amber-400/40'
                      : 'text-rose-700 dark:text-rose-300 bg-rose-500/15 ring-rose-500/40'
              }`}
              data-testid="bdc-nii-coverage-bucket"
              title={
                covRatio >= 1.0
                  ? 'NII fully covers the dividend — payout is being earned by the loan book.'
                  : covRatio >= 0.90
                    ? 'NII slightly under-covers the dividend. Watch for supplemental or special distributions filling the gap.'
                    : 'NII does NOT cover the dividend. This is the classic BDC yield-trap signal — a cut is more likely.'
              }
            >
              {covRatio >= 1.0
                ? <><CheckCircle2 className="w-3 h-3" /> Sustainable <span aria-hidden className="opacity-70">ⓘ</span></>
                : covRatio >= 0.90
                  ? <>Marginal <span aria-hidden className="opacity-70">ⓘ</span></>
                  : <><AlertTriangle className="w-3 h-3" /> At Risk <span aria-hidden className="opacity-70">ⓘ</span></>}
            </span>
          </div>

          {/* Simple bar showing coverage ratio, capped at 1.5x for
              display so an outlier quarter can't overflow. */}
          <div className="relative h-2 rounded-full bg-muted/40 overflow-hidden" data-testid="bdc-nii-coverage-bar">
            <div
              className={`absolute inset-y-0 left-0 ${
                covRatio >= 1.0
                  ? 'bg-emerald-500/70'
                  : covRatio >= 0.90 ? 'bg-amber-500/70' : 'bg-rose-500/70'
              }`}
              style={{ width: `${Math.max(2, Math.min(100, (covRatio / 1.5) * 100))}%` }}
            />
            {/* 1.0× marker */}
            <div className="absolute top-0 bottom-0 w-px bg-foreground/60" style={{ left: `${(1.0 / 1.5) * 100}%` }} />
          </div>
          <div className="flex items-center justify-between text-[9px] font-mono text-muted-foreground mt-0.5">
            <span>0.0×</span>
            <span className="font-bold text-foreground">1.0×</span>
            <span>1.5×</span>
          </div>

          {/* Quarter sparkline — only if we actually have ≥2 points. */}
          {sparkPoints.length >= 2 && (
            <div className="mt-1 flex items-end gap-0.5 h-6" title="Trailing quarterly NII coverage" data-testid="bdc-nii-sparkline">
              {sparkPoints.map((p, i) => {
                const h = Math.max(6, Math.min(100, (p.ratio / 1.5) * 100));
                const color = p.ratio >= 1.0 ? 'bg-emerald-500/70' : p.ratio >= 0.90 ? 'bg-amber-500/70' : 'bg-rose-500/70';
                return (
                  <div
                    key={p.end || i}
                    className={`flex-1 min-w-[6px] rounded-t ${color}`}
                    style={{ height: `${h}%` }}
                    title={`${p.end || `Q${i + 1}`}: ${p.ratio.toFixed(2)}×`}
                  />
                );
              })}
            </div>
          )}

          {/* Trend + latest-quarter caption */}
          <p className="text-[10px] text-muted-foreground italic leading-relaxed mt-1">
            {covTrend != null && (
              <>
                Trend{' '}
                <span className={`font-semibold ${covTrend >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                  {covTrend >= 0 ? '+' : ''}{covTrend.toFixed(1)}%
                </span>
                {sparkPoints.length >= 2 && <> over {sparkPoints.length}Q</>}
                {covLatestEnd && <> · latest Q ended {covLatestEnd}</>}
              </>
            )}
            {covTrend == null && covLatestEnd && <>Latest Q ended {covLatestEnd}.</>}
          </p>
        </div>
      )}
    </div>
  );
}
